import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X } from 'lucide-react';

export default function DropdownControls() {
  const { getAuthHeaders } = useAuth();
  const [controls, setControls] = useState({
    genders: [],
    categories: [],
    marital_status: [],
    employment_status: [],
    religions: [],
    academic_years: [],
    beneficiaries: [],
    payment_types: []
  });
  const [newValues, setNewValues] = useState({
    genders: '',
    categories: '',
    marital_status: '',
    employment_status: '',
    religions: '',
    academic_years: '',
    beneficiaries: '',
    payment_types: ''
  });
  const [loading, setLoading] = useState(true);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchControls();
  }, []);

  const fetchControls = async () => {
    try {
      const response = await axios.get(`${API_URL}/dropdown-controls`, {
        headers: getAuthHeaders()
      });
      const controlsMap = {
        genders: [],
        categories: [],
        marital_status: [],
        employment_status: [],
        religions: [],
        academic_years: [],
        beneficiaries: [],
        payment_types: []
      };
      response.data.forEach(control => {
        controlsMap[control.category] = control.values;
      });
      setControls(controlsMap);
    } catch (error) {
      toast.error('Failed to fetch dropdown controls');
    } finally {
      setLoading(false);
    }
  };

  const handleAddValue = async (category) => {
    if (!newValues[category].trim()) return;

    const updatedValues = [...controls[category], newValues[category].trim()];
    try {
      await axios.post(
        `${API_URL}/dropdown-controls`,
        { category, values: updatedValues },
        { headers: getAuthHeaders() }
      );
      setControls({ ...controls, [category]: updatedValues });
      setNewValues({ ...newValues, [category]: '' });
      toast.success('Value added successfully');
    } catch (error) {
      toast.error('Failed to add value');
    }
  };

  const handleRemoveValue = async (category, valueToRemove) => {
    const updatedValues = controls[category].filter(v => v !== valueToRemove);
    try {
      await axios.post(
        `${API_URL}/dropdown-controls`,
        { category, values: updatedValues },
        { headers: getAuthHeaders() }
      );
      setControls({ ...controls, [category]: updatedValues });
      toast.success('Value removed successfully');
    } catch (error) {
      toast.error('Failed to remove value');
    }
  };

  const ControlSection = ({ title, category }) => (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">{title}</h3>
      <div className="flex gap-2 mb-4">
        <Input
          value={newValues[category]}
          onChange={(e) => setNewValues({ ...newValues, [category]: e.target.value })}
          placeholder="Add new value"
          data-testid={`${category}-input`}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddValue(category))}
        />
        <Button
          onClick={() => handleAddValue(category)}
          data-testid={`${category}-add-button`}
          className="bg-[#0052CC] hover:bg-[#0043A8] text-white whitespace-nowrap"
        >
          Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {controls[category].map((value, index) => (
          <div
            key={index}
            className="flex items-center gap-2 px-3 py-2 bg-slate-100 text-slate-700 rounded-md text-sm"
            data-testid={`${category}-value-${index}`}
          >
            <span>{value}</span>
            <button
              onClick={() => handleRemoveValue(category, value)}
              className="text-slate-500 hover:text-red-600"
              data-testid={`${category}-remove-${index}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      {controls[category].length === 0 && (
        <p className="text-sm text-slate-500 italic">No values added yet</p>
      )}
    </div>
  );

  return (
    <Sidebar>
      <div className="max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Dropdown Controls
          </h1>
          <p className="text-slate-600 mt-2">Manage dropdown values for admission forms</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading controls...</div>
        ) : (
          <div className="space-y-6">
            <ControlSection title="Gender Options" category="genders" />
            <ControlSection title="Category Options" category="categories" />
            <ControlSection title="Marital Status Options" category="marital_status" />
            <ControlSection title="Employment Status Options" category="employment_status" />
            <ControlSection title="Religion Options" category="religions" />
            <ControlSection title="Academic Years" category="academic_years" />
            <ControlSection title="Payment Beneficiaries" category="beneficiaries" />
            <ControlSection title="Payment Types" category="payment_types" />
          </div>
        )}
      </div>
    </Sidebar>
  );
}